import * as React from 'react';
import { User, ChatContentSettings } from '@wix/answers-api';
import { UserAvatar, Popover, DeprecatedText } from '@wix/answers-ui-libby';
import { getIdealTextColor, classNames, noop } from '@wix/answers-lib';
import { TranslateFn } from '@wix/answers-app-core';

export const chatRoomHeaderKey = 'chat-room-header';

export type ChatRoomHeaderProps = {
	// in
	config: ChatContentSettings;
	backgroundColor: string;
	fontFamily: string;
	participants: User[];
	messageCount: number;
	activeAgents: User[];
	t: TranslateFn;
	hideBack?: boolean;
	windowHeight: number;
	windowWidth: number;
	isMobile: boolean;

	// out
	onBack: () => void;
};

const SMALL_WINDOW_HEIGHT = 600;
const KEYBOARD_OPEN_HEIGHT = 500;
const SMALL_WINDOW_KEYBOARD_OPEN_HEIGHT = 400;

export type ChatRoomHeaderState = {
	participantsOpen: boolean;
};

export class ChatRoomHeader extends React.PureComponent<ChatRoomHeaderProps, ChatRoomHeaderState> {

	state: ChatRoomHeaderState = {
		participantsOpen: false
	};

	getParticipantNames = (agents: User[]) => {
		if (agents.length < 3) {
			return agents.map((agent) => agent.firstName).join(' & ');
		} else {
			const lastAgent = agents[agents.length - 1];
			const restAgents = agents.slice(0, agents.length - 1).map((agent) => agent.firstName).join(', ');
			return `${restAgents} & ${lastAgent.firstName}`;
		}
	}

	openParticipants = () => this.setState({participantsOpen: true});
	closeParticipants = () => this.setState({participantsOpen: false});

	getParticipantAvatars = (agents: User[]) => {
		if (agents.length < 3) {
			return <div className='participant-avatars'>{agents.map(this.renderParticipantAvatar)}</div>;
		} else {
			const renderAdditionalParticipant = (participant: User, i: number) => (
				<div className={'additional-participant'} key={participant.id}>
					{this.renderParticipantAvatar(participant, i + 1)}<DeprecatedText>{participant.firstName}</DeprecatedText>
				</div>
			);
			const additionalParticipants = (
				<div className='additional-participants-popover-body'>
					{agents.slice(1).map(renderAdditionalParticipant)}
				</div>
			);
			return (
				<div className='participant-avatars'>
					{this.renderParticipantAvatar(agents[0], 0)}
					<Popover body={additionalParticipants} isOpen={this.state.participantsOpen} onOuterAction={noop}>
						<div
							className='additional-agent-avatars-wrapper'
							onMouseEnter={this.openParticipants}
							onMouseLeave={this.closeParticipants}
							style={{background: this.props.backgroundColor}}
						>
							<div className='additional-agent-avatars'>{`+${agents.length - 1}`}</div>
						</div>
					</Popover>
				</div>
			);
		}
	}

	renderAgentAvatar = (agent: User, i: number) => {
		const short = this.props.windowHeight < SMALL_WINDOW_HEIGHT;
		return (
			<div key={i} className='agent-avatar'>
				<UserAvatar size={short ? 'small' : 'normal'} user={agent} isAgent={true} />
			</div>
		);
	}

	renderParticipantAvatar = (agent: User, i: number) => (
		<div key={i} className='participant-avatar' style={{background: this.props.backgroundColor}}>
			<UserAvatar size='small' user={agent} isAgent={true} />
		</div>
	)

	render () {
		const {props} = this;
		const {windowHeight} = props;
		const {title, subTitle, subTitleEnabled, responseTimeMessage,
									responseTimeMessageEnabled, displayAvatarsEnabled} = props.config;
		const empty = !props.participants.length;
		const defaultResponseTime = props.t('widget-ng.chat.defaults.response-time');

		const agentAvatars = (
			<div className='avatars-wrapper'>
				{props.activeAgents.map(((agent, i) => this.renderAgentAvatar(agent, i)))}
			</div>
		);

		const emptyRoomClassnames = classNames(
			'empty-room-wrapper',
			{
				'no-subtitle': !subTitleEnabled,
				'no-response-msg': !responseTimeMessageEnabled,
				'no-avatars': !displayAvatarsEnabled
			}
		);
		const emptyRoom = (
			<div className={emptyRoomClassnames}>
				<div className='title'>{title || props.t('widget-ng.chat.defaults.title')}</div>
				{subTitleEnabled ? <div className='subtitle'>{subTitle || props.t('widget-ng.chat.defaults.subtitle')}</div> : null}
				{displayAvatarsEnabled ? agentAvatars : null}
				{responseTimeMessageEnabled ? <div className='reply-time'>{responseTimeMessage || defaultResponseTime}</div> : null}
			</div>
		);

		const participants = (
			<div className='participants-wrapper'>
				{this.getParticipantAvatars(props.participants)}
				<div className='participants'>{this.getParticipantNames(props.participants)}</div>
			</div>
		);

		const style = {
			background: props.backgroundColor,
			color: getIdealTextColor(props.backgroundColor),
			fontFamily: props.fontFamily
		};

		const isShortWindow = (windowHeight < SMALL_WINDOW_HEIGHT && windowHeight > KEYBOARD_OPEN_HEIGHT)
			|| windowHeight < SMALL_WINDOW_KEYBOARD_OPEN_HEIGHT;
		const classnames = classNames(
			chatRoomHeaderKey,
			{
				empty,
				'no-back': props.hideBack,
				'dark-background': style.color === '#FFFFFF',
				mobile: this.props.isMobile,
				short: isShortWindow,
				'keyboard-open': windowHeight < KEYBOARD_OPEN_HEIGHT
			}
		);

		return (
			<div className={classnames} style={style}>
				<div className='back-btn-wrapper'>
					{!props.hideBack ? <div className='back' onClick={props.onBack}><div className='back-arrow'/></div> : null}
					{props.messageCount ? <div className='count'>{props.messageCount}</div> : null}
				</div>
				{!empty ? participants : emptyRoom}
			</div>
		);
	}
}
